import { BillSlipStatus } from "@boo/shared";
import type { PublicBillPerson } from "@boo/shared";

const STEPS = ["Pick items", "Confirm", "Pay"] as const;

function describe(
  open: boolean,
  me: PublicBillPerson,
  hasClaim: boolean,
  hasFlag: boolean,
): { step: number; emoji: string; text: string } {
  if (open) {
    if (hasFlag)
      return {
        step: 1,
        emoji: "🚩",
        text: "The owner is looking at your flag",
      };
    if (me.reviewed)
      return {
        step: 2,
        emoji: "🙌",
        text: "You're good. Waiting for the owner to finalise",
      };
    if (hasClaim)
      return {
        step: 1,
        emoji: "👀",
        text: "Check your share, then tap confirm",
      };
    return { step: 0, emoji: "👆", text: "Tap the items you had" };
  }
  if (me.isPaid) return { step: 3, emoji: "🎉", text: "All settled. Thanks!" };
  if (me.slipStatus === BillSlipStatus.Pending)
    return {
      step: 2,
      emoji: "⏳",
      text: "Payment sent, the owner is checking",
    };
  if (me.slipStatus === BillSlipStatus.Rejected)
    return { step: 2, emoji: "😬", text: "Slip didn't pass. Upload it again" };
  return { step: 2, emoji: "💸", text: "Ready to pay. Scan the QR below" };
}

/** Tells the friend where the bill stands and what to do next. */
export default function BillProgress({
  open,
  me,
  hasClaim,
  hasFlag,
}: {
  open: boolean;
  me: PublicBillPerson;
  hasClaim: boolean;
  hasFlag: boolean;
}) {
  const { step, emoji, text } = describe(open, me, hasClaim, hasFlag);
  return (
    <div className="mt-3 rounded-xl border border-border bg-card p-4">
      <p
        key={text}
        aria-live="polite"
        className="bill-fade-up text-base font-semibold text-foreground"
      >
        <span className="mr-2" aria-hidden>
          {emoji}
        </span>
        {text}
      </p>
      <ol className="mt-3 grid grid-cols-3 gap-2">
        {STEPS.map((label, i) => (
          <li key={label}>
            <div
              className={`h-1 rounded-full transition-colors duration-500 ${
                i < step ? "bg-primary" : "bg-muted"
              }`}
            />
            <div
              className={`mt-1 text-xs ${
                i < step ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {label}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
